export type ServiceRef =
  | number
  | { id?: number | null; name?: string | null; attributes?: { name?: string } }
  | null
  | undefined;

export type UserWithServices = {
  service?: ServiceRef;
  services?: ServiceRef[] | ServiceRef;
  allowed_services?: ServiceRef[] | ServiceRef;
} | null;

/** Legacy attached beneficiaries that receive classic ebooks (bilan / reconversion). */
export const RATTACHED_BENEFICIARY_SERVICE_IDS = [1, 5];

/** Services that unlock needs tests on the compass. */
export const NEEDS_TEST_SERVICE_IDS = [3, 5];

/** OA Match eligible services for attached users. */
export const OA_MATCH_SERVICE_IDS = [1, 5];

/**
 * Normalize a services payload (ids or objects) into unique numeric ids.
 * Mirrors backend `orient-action-api/src/utils/service-access.js`.
 */
export const normalizeServiceIds = (services: ServiceRef[] | ServiceRef): number[] => {
  if (services == null) {
    return [];
  }

  const list = Array.isArray(services) ? services : [services];

  return Array.from(
    new Set(
      list
        .map(service => {
          if (service == null) {
            return null;
          }
          if (typeof service === 'object') {
            return service.id ?? null;
          }
          const parsed = Number(service);
          return Number.isNaN(parsed) ? null : parsed;
        })
        .filter((id): id is number => Boolean(id)),
    ),
  );
};

const getPrimaryServiceId = (service: ServiceRef): number | null => {
  if (service == null) {
    return null;
  }
  if (typeof service === 'object') {
    return service.id ?? null;
  }
  const parsed = Number(service);
  return Number.isNaN(parsed) ? null : parsed;
};

/**
 * Build the list of service ids used for resource access.
 * Includes the primary `service` and the multi-service relation `services`.
 */
export const getUserServiceIds = (user?: UserWithServices): number[] => {
  if (!user) {
    return [];
  }

  const ids = normalizeServiceIds(user.services);
  const primaryId = getPrimaryServiceId(user.service);

  if (primaryId && !ids.includes(primaryId)) {
    ids.unshift(primaryId);
  }

  return ids;
};

/** Primary service object/id when present (display / legacy single-service UI). */
export const getPrimaryService = <T extends ServiceRef>(user?: {
  service?: T;
} | null): T | undefined => user?.service;

export const userHasService = (user: UserWithServices, serviceId: number): boolean =>
  getUserServiceIds(user).includes(Number(serviceId));

export const userHasAnyService = (
  user: UserWithServices,
  serviceIds: number[] = [],
): boolean => {
  const userIds = getUserServiceIds(user);
  return serviceIds.some(id => userIds.includes(Number(id)));
};

/** Manager capability via `allowed_services` (kept separate from beneficiary multi-services). */
export const userHasAllowedService = (
  user: UserWithServices,
  serviceId: number,
): boolean => normalizeServiceIds(user?.allowed_services).includes(Number(serviceId));

/**
 * Compass ("Mon profil") is hidden when the user only has service 2.
 * Multi-service users that also have other services keep access.
 */
export const shouldHideCompass = (user?: UserWithServices): boolean => {
  const ids = getUserServiceIds(user);
  return ids.length > 0 && ids.every(id => id === 2);
};

/**
 * Resolve compass test visibility flags across primary + multi services.
 */
export const getCompassTestVisibility = (user?: UserWithServices) => {
  const rawServices = user?.services;
  const multiServices: ServiceRef[] = Array.isArray(rawServices)
    ? rawServices
    : rawServices != null
    ? [rawServices]
    : [];

  const services = [user?.service, ...multiServices].filter(Boolean) as Array<
    { testToShow?: string } | number
  >;

  const values = services
    .map(service => (typeof service === 'object' ? service.testToShow : undefined))
    .filter(Boolean) as string[];

  const showAllTest = values.includes('all');
  return {
    showAllTest,
    showNeeds: showAllTest || values.includes('needs'),
    showNeedsPro: showAllTest || values.includes('needsPro'),
  };
};
